import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";

import MDBox from "@/components/atoms/MDBox";
import { ProductFormData } from "@/components/screens/ProductFormPage/types";
import { useProductFormStateContext } from "@/contexts/product-form-state-context";
import useDialog from "@/hooks/use-dialog";
import { useSkuImageValidation } from "@/hooks/use-sku-image-validation";
import { ISku } from "@/interfaces/sku.interface";
import { ProductFormPageMessages } from "@/messages/products/product-form-page.messages";

import SkuFormDialog from "./components/SkuFormDialog";
import { SkuTable } from "./components/SkuTable";

type Props = {
  messages: ProductFormPageMessages;
  sku: ISku | null;
};

const SkuVariationForm = ({ messages, sku }: Props) => {
  const { isVisible, handleClose, handleOpen } = useDialog<ISku>();
  const { values, setFieldValue } = useFormikContext<ProductFormData>();
  const { ensureProductSaved, savedImages, reloadImages } = useProductFormStateContext();
  const { validateAndCleanSkuImages } = useSkuImageValidation();

  const handleOpenDialog = async (isEditing: boolean) => {
    // Garantir que o produto está salvo antes de criar/editar SKU
    const isSaved = await ensureProductSaved();
    
    if (!isSaved) {
      return;
    }

    // Recarregar imagens para garantir sincronia
    await reloadImages();
    await new Promise((resolve) => setTimeout(resolve, 150));

    // Validar e limpar SKUs com imagens inválidas
    const { cleaned, skus } = validateAndCleanSkuImages();
    if (cleaned > 0) {
      setFieldValue("skus", skus);
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    setFieldValue("isEditingSku", isEditing);
    handleOpen();
  };

  const handleDeleteFromForm = (sku: ISku) => {
    if (!sku) return;

    const updatedSkus = values.skus.filter((s) => s !== sku);
    setFieldValue("skus", updatedSkus);
    handleClose();
  };

  const confirmDelete = async (sku: ISku) => {
    if ("id" in sku && sku.id) {
      const updatedSkusToDelete = [...values.skusToDelete, sku.id];
      setFieldValue("skusToDelete", updatedSkusToDelete);
      handleDeleteFromForm(sku);
    } else {
      handleDeleteFromForm(sku);
    }
  };

  const handleAddSkuClick = async () => {
    await handleOpenDialog(false);
  };

  return (
    <MDBox mt={2}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 12 }}>
          <SkuTable
            messages={messages}
            skus={values?.skus}
            onRemove={confirmDelete}
            addSkuClick={handleAddSkuClick}
            openDialog={handleOpenDialog}
          />
        </Grid>
      </Grid>

      <SkuFormDialog
        isVisible={isVisible}
        handleClose={handleClose}
        messages={messages}
        sku={sku}
        isEditingSku={values.isEditingSku}
      />
    </MDBox>
  );
};

export default SkuVariationForm;
